import React from 'react';
import { Link } from 'react-router-dom';
import { Container, Row, Col } from 'react-bootstrap';

import { useEcosystem } from '@/shared/contexts/EcosystemContext';
import useCapability from '@/shared/hooks/useCapability';
import {
    getTenantLogoUrl,
    RILE_HORIZONTAL_LOGO_URL,
} from '@/shared/utils/brandAssets';

import './Footer.css';

// Apex-mode default copy. Tagline + copyright fall back to these strings on
// the platform apex when the platform pseudo-Ecosystem has no
// branding-authored tagline / copyright (Wave M will add those schema fields).
const APEX_TAGLINE =
    'RILE Connect is open infrastructure for hosting learning ' +
    'ecosystems, making pathways visible, navigable, and equitable ' +
    'from classroom to career.';
const APEX_BRAND_NAME = 'RILE Connect';

// Tenant-mode default tagline used when the active ecosystem has no
// branding.tagline authored. Generic enough to read sensibly for any
// hosted ecosystem without misrepresenting it.
const tenantDefaultTagline = (ecosystemName) =>
    `${ecosystemName} on RILE Connect, making pathways visible, ` +
    `navigable, and equitable from classroom to career.`;

// Platform-apex link set (links to apex routes). Each entry: { to, label }.
const APEX_LINKS = {
    explore: [
        { to: '/about', label: 'About RILE Connect' },
        { to: '/mission', label: 'Mission' },
        { to: '/ecosystems', label: 'Ecosystems' },
    ],
    connect: [
        { to: '/contact', label: 'Contact Us' },
        { to: '/policies', label: 'Policies' },
        { to: '/feedback', label: 'Send Feedback' },
    ],
};

// Tenant-mode link set (links resolved relative to the active tenant via
// React Router's basename: Link `to="/directory"` becomes `/costem/directory`
// in path mode automatically). Per-link `requires` follows the NavBar
// convention (see NavBar.jsx for the filter semantics).
const TENANT_LINKS = {
    explore: [
        { to: '/about', label: 'About Us' },
        { to: '/mission', label: 'Mission' },
        { to: '/directory', label: 'Ecosystem' },
        {
            to: '/opportunities',
            label: 'Opportunities',
            requires: 'opportunities',
        },
        { to: '/policies', label: 'Policies' },
    ],
    // 'Send Feedback' is intentionally apex-only: feedback is gathered at the
    // platform level, not per hosted ecosystem (the /feedback route still
    // exists — this removes only the tenant entry point). See APEX_LINKS.connect.
    connect: [{ to: '/contact', label: 'Contact Us' }],
};

export default function Footer() {
    const { ecosystem, isPlatform } = useEcosystem();
    const year = new Date().getFullYear();

    // Per-tenant capability flags. Read up-front (apex returns true from
    // every flag). Drives `requires`-based filtering on the link sets
    // below (see NavBar.jsx for the convention).
    const capOpportunities = useCapability('opportunities');
    const capabilityFlags = {
        opportunities: capOpportunities,
    };
    const filterByCapability = (link) => {
        if (!link.requires) return true;
        if (Array.isArray(link.requires)) {
            return link.requires.some((cap) => capabilityFlags[cap]);
        }
        return capabilityFlags[link.requires];
    };

    // Schema-field caveat (Wave M will add these fields to Ecosystem.branding):
    //   - branding.tagline
    //   - branding.copyright
    //   - branding.socialLinks.linkedin
    // Until then, optional-chained reads fall back to sensible defaults so the
    // UI renders cleanly for both COSTEM and the apex without authored content.
    const branding = ecosystem?.branding;
    const logoUrl = getTenantLogoUrl(ecosystem);
    // Same rule as NavBar: the horizontal lockup has "RILE Connect" baked
    // into the image, so it's only correct on the actual platform apex
    // (isPlatform).

    let tagline;
    let copyright;
    let linkedinUrl;
    let brandName;
    let links;

    if (isPlatform) {
        tagline = branding?.tagline || APEX_TAGLINE;
        copyright = branding?.copyright || `© ${year} RILE Connect`;
        // The platform itself has no LinkedIn account today; render only when
        // explicitly authored. Avoids fabricating a social presence.
        linkedinUrl = branding?.socialLinks?.linkedin || null;
        brandName = APEX_BRAND_NAME;
        links = APEX_LINKS;
    } else {
        const name = ecosystem?.name || 'This ecosystem';
        tagline = branding?.tagline || tenantDefaultTagline(name);
        // Tenant copyright: when not authored, fall back to the ecosystem
        // name itself. NOT a hardcoded host-institution string: a non-COSTEM
        // tenant would be misattributed.
        copyright = branding?.copyright || `© ${year} ${name}`;
        linkedinUrl = branding?.socialLinks?.linkedin || null;
        brandName = ecosystem?.name || APEX_BRAND_NAME;
        links = TENANT_LINKS;
    }

    return (
        <div id="footer" className="bg-light px-3 px-md-0">
            <Container className="footer-container">
                <Row className="align-items-start gy-3">
                    {/* Brand + copyright */}
                    <Col md={5}>
                        <Link
                            to="/"
                            className="d-flex align-items-center gap-2 text-decoration-none footer-title mb-2"
                        >
                            {isPlatform ? (
                                <img
                                    src={RILE_HORIZONTAL_LOGO_URL}
                                    alt={brandName}
                                    className="footer-brand-logo-horizontal"
                                />
                            ) : (
                                <>
                                    {logoUrl && (
                                        <img
                                            src={logoUrl}
                                            alt={`${brandName} logo`}
                                            className="footer-brand-logo"
                                        />
                                    )}
                                    <span className="h4 mb-0">{brandName}</span>
                                </>
                            )}
                        </Link>
                        <p className="text-muted small mb-0">{tagline}</p>
                        <p className="text-muted small mt-2 mb-0">
                            {copyright}
                        </p>
                    </Col>

                    {/* Navigation links */}
                    <Col md={{ span: 3, offset: 1 }}>
                        <h6 className="text-primary fw-semibold mb-2">
                            Explore
                        </h6>
                        <ul className="list-unstyled footer-links mb-0">
                            {links.explore
                                .filter(filterByCapability)
                                .map((link) => (
                                    <li key={link.to}>
                                        <Link
                                            to={link.to}
                                            className="footer-link"
                                        >
                                            {link.label}
                                        </Link>
                                    </li>
                                ))}
                        </ul>
                    </Col>

                    {/* Connect + feedback */}
                    <Col md={3}>
                        <h6 className="text-primary fw-semibold mb-2">
                            Connect
                        </h6>
                        <ul className="list-unstyled footer-links mb-0">
                            {links.connect
                                .filter(filterByCapability)
                                .map((link) => (
                                    <li key={link.to}>
                                        <Link
                                            to={link.to}
                                            className="footer-link"
                                        >
                                            {link.label}
                                        </Link>
                                    </li>
                                ))}
                            {linkedinUrl && (
                                <li>
                                    <a
                                        href={linkedinUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="footer-link"
                                    >
                                        LinkedIn
                                    </a>
                                </li>
                            )}
                        </ul>
                    </Col>
                </Row>
            </Container>
        </div>
    );
}
